

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  isDeleting?: boolean;
  confirmationText?: string;
  itemName?: string;
  itemNameLines?: readonly string[];
}

export function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  isDeleting = false,
  confirmationText,
  itemName,
  itemNameLines,
}: Readonly<DeleteConfirmationModalProps>) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const finalTitle = title ?? t('ui.delete_modal.default_title');
  const finalDescription = description ?? t('ui.delete_modal.default_description');
  const finalConfirmationText = confirmationText ?? t('ui.delete_modal.default_confirmation_text');

  // Reset input and manage focus when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
    } else {
      // Need a small timeout to allow Dialog transition to start before focusing
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const isConfirmed = inputValue === finalConfirmationText;

  const handleConfirm = () => {
    if (isConfirmed) {
      onConfirm();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{finalTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText paragraph>
          {finalDescription}
        </DialogContentText>
        {(itemName || itemNameLines?.length) && (
          <Box component="div" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
            {t('ui.delete_modal.item_to_delete')}
            {itemNameLines?.length ? (
              <Box sx={{ mt: 0.5, pl: 2, fontWeight: 'normal' }}>
                {itemNameLines.map((line, index) => (
                  <Box component="div" key={`${line}-${index}`}>
                    {line}
                  </Box>
                ))}
              </Box>
            ) : (
              <> {itemName}</>
            )}
          </Box>
        )}

        <Box sx={{ my: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            {t('ui.delete_modal.please_type')} <Box component="span" sx={{ fontWeight: 'bold' }}>{finalConfirmationText}</Box> {t('ui.delete_modal.to_confirm')}
          </Typography>
          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            inputRef={inputRef}
            placeholder={finalConfirmationText}
            fullWidth
            variant="outlined"
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting} color="inherit">
          {t('ui.delete_modal.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!isConfirmed || isDeleting}
          color="error"
          variant="contained"
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isDeleting ? t('ui.delete_modal.deleting') : t('ui.delete_modal.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
